import { useState } from 'react';
import {AddressBook,
  Briefcase, Buildings, CalendarBlank, CalendarX, CaretDown, ChatsCircle, ClipboardText, EnvelopeSimple, Folders, FolderSimple, Gauge, GearSix,
  HouseLine, IdentificationBadge, IdentificationCard, Key, Lightning, LockKey, MagnifyingGlass, PaperPlaneTilt, Receipt, Signature,
  SquaresFour, Ticket, Timer, Users, UsersThree, Wrench, type Icon,
} from '@phosphor-icons/react';
import { HAUPT_NAV, baueNavGruppen, gruppeVon, istAktiv, type HauptNavId, type NavGruppe } from '../../../lib/immobilien/sidebarNav';
import { isItemVisible, type VisibilityContext } from '../../../lib/sidebar/navigation';
import { SIDEBAR_CATEGORIES, TOP_LEVEL_ITEMS } from '../../admin/sidebarConfig';
import { ThemeUmschalter } from '../ui/ThemeUmschalter';
import { NavZeile, ZaehlBadge } from './NavZeile';

export const HAUPT_ICONS: Record<HauptNavId, Icon> = {
  dashboard: SquaresFour,
  objekte: Buildings,
  mieter: Users,
  eigentuemer: Key,
  dienstleister: Wrench,
  kontakte: AddressBook,
  rechnungen: Receipt,
  dokumente: FolderSimple,
  tickets: Ticket,
  kalender: CalendarBlank,
  vertraege: Signature,
  zaehler: Gauge,
  emails: EnvelopeSimple,
  team: UsersThree,
};

const ALLE_ITEMS = [...TOP_LEVEL_ITEMS, ...SIDEBAR_CATEGORIES.flatMap(c => c.items)];

/** Symbole der übrigen Bereiche und der Gruppen */
const WEITERE_ICONS: Record<string, Icon> = {
  schreiben: PaperPlaneTilt, mieterselbstauskunft: IdentificationCard, uebergabeprotokolle: ClipboardText, zeiterfassung: Timer,
  abwesenheiten: CalendarX, personalakte: IdentificationBadge, monatsabschluss: LockKey, 'super-admin': Lightning,
};
const GRUPPEN_ICONS: Record<string, Icon> = {
  dashboard: SquaresFour, immobilien: Buildings, kontakte: AddressBook, vermietung: HouseLine, kommunikation: ChatsCircle,
  finanzen: Folders, personal: Briefcase, system: GearSix, weitere: SquaresFour,
};
const iconVon = (id: string): Icon => (HAUPT_ICONS as Record<string, Icon>)[id] ?? WEITERE_ICONS[id] ?? SquaresFour;

const OFFEN_SCHLUESSEL = 'immo.nav.offeneGruppen';
const leseOffen = (): string[] => { try { const v = JSON.parse(localStorage.getItem(OFFEN_SCHLUESSEL) ?? '[]'); return Array.isArray(v) ? v : []; } catch { return []; } };
const schreibeOffen = (ids: string[]) => { try { localStorage.setItem(OFFEN_SCHLUESSEL, JSON.stringify(ids)); } catch { /* ohne Speicher: nur für diese Sitzung */ } };

export interface Badges {
  rechnungenBadge: number;
  emailBadge: number;
  vorschlaegeBadge: number;
  unreadByModul: Record<string, number>;
}

export function ListenModus({ aktiveId, sichtbarkeit, badges, laedt, onNavigate, onSuche }: {
  aktiveId: string;
  /** Module werden noch geladen (Team-Nutzer sehen sonst kurz eine leere Navigation) */
  laedt: boolean;
  sichtbarkeit: VisibilityContext;
  badges: Badges;
  onNavigate: (pfad: string) => void;
  onSuche: () => void;
}) {
  // Patrick 01.11.2026: Überbereiche zum Aufklappen statt langer Liste + „Weitere Bereiche“
  const [offen, setOffen] = useState<string[]>(leseOffen);
  const haupt = new Map<string, string>(HAUPT_NAV.map(n => [n.id, n.label]));
  const sichtbar = ALLE_ITEMS
    .filter(i => !i.comingSoon && isItemVisible(i, sichtbarkeit))
    .map(i => ({ id: i.id, label: haupt.get(i.id) ?? i.label, path: i.path }));
  const gruppen = baueNavGruppen(sichtbar);
  const aktiveGruppe = gruppeVon(gruppen, aktiveId);

  const umschalten = (id: string) => setOffen(o => { const n = o.includes(id) ? o.filter(x => x !== id) : [...o, id]; schreibeOffen(n); return n; });

  const rechts = (id: string) => {
    if (id === 'rechnungen' && badges.rechnungenBadge > 0) return <ZaehlBadge anzahl={badges.rechnungenBadge} ton="bad" titel="Rechnungen fällig in ≤ 3 Tagen" />;
    if (id === 'emails' && (badges.emailBadge > 0 || badges.vorschlaegeBadge > 0)) {
      return (
        <span className="flex" style={{ gap: 4 }}>
          {badges.vorschlaegeBadge > 0 && <ZaehlBadge anzahl={badges.vorschlaegeBadge} ton="acc" titel="Offene KI-Vorschläge" />}
          {badges.emailBadge > 0 && <ZaehlBadge anzahl={badges.emailBadge} ton="info" titel="Ungelesene E-Mails" />}
        </span>
      );
    }
    if ((badges.unreadByModul[id] ?? 0) > 0) return <Punkt />;
    return undefined;
  };

  /** Zugeklappte Gruppe: Zahl der Einträge zusammengefasst, sonst ein Punkt bei Neuem */
  const gruppenRechts = (g: NavGruppe) => {
    const ids = g.eintraege.map(e => e.id);
    const zahl = (ids.includes('rechnungen') ? badges.rechnungenBadge : 0) + (ids.includes('emails') ? badges.emailBadge : 0);
    if (zahl > 0) return <ZaehlBadge anzahl={zahl} ton={ids.includes('emails') ? 'info' : 'bad'} titel="Neues in diesem Bereich" />;
    if (ids.some(id => (badges.unreadByModul[id] ?? 0) > 0) || (ids.includes('emails') && badges.vorschlaegeBadge > 0)) return <Punkt />;
    return undefined;
  };

  return (
    <>
      <ThemeUmschalter />
      <button
        type="button"
        onClick={onSuche}
        className="flex items-center cursor-pointer text-left"
        style={{ gap: 8, padding: '8px 10px', borderRadius: 12, border: 'none', background: 'var(--fill2)', color: 'var(--ink3)', fontSize: 13 }}
      >
        <MagnifyingGlass size={15} color="var(--ink3)" />
        <span className="flex-1">Suchen</span>
        <span style={{ fontSize: 11 }}>⌘K</span>
      </button>
      <nav aria-label="Hauptnavigation" className="immo-ohne-scrollbar flex flex-col min-h-0 overflow-auto" style={{ gap: 1 }}>
        {laedt && !sichtbarkeit.isSuperAdmin && Array.from({ length: 8 }, (_, i) => (
          <span key={i} aria-hidden className="animate-pulse" style={{ height: 24, margin: '7px 9px', borderRadius: 8, background: 'var(--fill2)' }} />
        ))}
        {(!laedt || sichtbarkeit.isSuperAdmin) && gruppen.map(g => {
          if (g.einzeln) {
            const e = g.eintraege[0];
            return <NavZeile key={g.id} art="liste" label={e.label} icon={iconVon(e.id)} aktiv={istAktiv(e, aktiveId)} onClick={() => onNavigate(e.path)} rechts={rechts(e.id)} />;
          }
          const auf = offen.includes(g.id) || aktiveGruppe === g.id;
          const GIcon = GRUPPEN_ICONS[g.id] ?? SquaresFour;
          return (
            <div key={g.id} className="flex flex-col" style={{ gap: 1, marginTop: 4 }}>
              {/* #594: Gruppenkopf abgesetzt – eigene Fläche, Symbol in Kachel, runder Auf/Zu-Knopf */}
              <button
                type="button"
                aria-expanded={auf}
                onClick={() => umschalten(g.id)}
                data-tip={auf ? `${g.label} zuklappen` : `${g.label} aufklappen`}
                className="immo-hover-flaeche flex items-center w-full cursor-pointer text-left"
                style={{ gap: 10, padding: '6px 8px', borderRadius: 12, border: 'none', background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)',
                  color: 'var(--ink)', fontSize: 13, fontWeight: 800, letterSpacing: '-0.01em' }}
              >
                <span className="flex items-center justify-center shrink-0" style={{ width: 26, height: 26, borderRadius: 8,
                  background: aktiveGruppe === g.id ? 'var(--acc-soft)' : 'var(--fill2)' }}>
                  <GIcon weight={aktiveGruppe === g.id ? 'fill' : 'duotone'} size={17} color={aktiveGruppe === g.id ? 'var(--acc)' : 'var(--ink2)'} />
                </span>
                <span className="flex-1 min-w-0 truncate">{g.label}</span>
                {!auf && gruppenRechts(g)}
                <span aria-hidden className="flex items-center justify-center shrink-0" style={{ width: 22, height: 22, borderRadius: 11, background: 'var(--fill2)' }}>
                  <CaretDown weight="bold" size={11} color="var(--ink2)" style={{ transition: 'transform .2s', transform: auf ? 'rotate(180deg)' : 'none' }} />
                </span>
              </button>
              {auf && (
                <div className="flex flex-col" style={{ gap: 1, marginLeft: 20, paddingLeft: 6, borderLeft: '1px solid var(--line)', marginBottom: 2 }}>
                  {g.eintraege.map(e => (
                    <NavZeile key={e.id} art="liste" label={e.label} icon={iconVon(e.id)} aktiv={istAktiv(e, aktiveId)} onClick={() => onNavigate(e.path)} rechts={rechts(e.id)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </>
  );
}

function Punkt() {
  return <span title="Neue Benachrichtigungen" style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--hl)', flexShrink: 0 }} />;
}
